import { Route as SettingsTenantRoute } from 'src/routes/settings/tenant';
import React from 'react';
import { SettingName } from 'src/api/generated';
import { SidebarButton } from 'src/components/atoms/buttons/sidebar-button/sidebar-button';
import { SidebarGroupsSkeleton } from './sidebar-groups-skeleton';
import {
  PiChartBarLight,
  PiPhoneLight,
  PiBookOpenTextLight,
  PiUsersLight,
  PiPlugLight,
  PiChatsCircleLight,
  PiBellLight,
  PiCalendarLight,
  PiCirclesThreeLight,
  PiPackageLight,
  PiTableLight,
  PiTreeStructureLight,
  PiBuildingLight,
  PiKeyLight,
  PiCaretDown,
  PiGlobeLight,
  PiUserCircleLight,
  PiGearLight,
} from 'react-icons/pi';
import { AiRobotOutlineIcon } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Route as ReportsPanelIndexRoute } from 'src/routes/reports-panel.index';
import { Route as CallsIndexRoute } from 'src/routes/calls.index';
import { Route as ContactsIndexRoute } from 'src/routes/contacts.index';
import { Route as CommunicationsIndexRoute } from 'src/routes/communications/index';
import { Route as UsersIndexRoute } from 'src/routes/users';
import { Route as CalendarIndexRoute } from 'src/routes/calendar/index';
import { Route as EmployeesIndexRoute } from 'src/routes/employees/index';
import { Route as AutomationsIndexRoute } from 'src/routes/automations.index';
import { Route as CatalogProductsRoute } from 'src/routes/catalog/products';
import { Route as CatalogPropertiesRoute } from 'src/routes/catalog/properties.index';
import { Route as SettingsCommunicationsRoute } from 'src/routes/settings/funnels';
import { Route as SettingsIntegrationsRoute } from 'src/routes/settings/integrations.index';
import { Route as SettingsCustomFieldsRoute } from 'src/routes/settings/custom-fields';
import { Route as SettingsCredentialsRoute } from 'src/routes/settings/credentials';
import { Route as SettingsNotificationsRoute } from 'src/routes/settings/notifications';
import { Route as SettingsWebsiteTemplateRegistryRoute } from 'src/routes/settings/website-template-registry';
import { Route as SettingsWebsitesRoute } from 'src/routes/settings/websites';
import { Route as SettingsPreferencesRoute } from 'src/routes/settings/preferences';
import { useMatchRoute } from '@tanstack/react-router';
import { useFeatureFlagActive } from 'hooks/settings/feature-flags';
import { useBooleanSetting } from 'hooks/settings/settings';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useClientType } from 'src/modules/shared/hooks/use-client-type';
import {
  AutomationEmployeePermission,
  AutomationWorkflowPermission,
  CallPermission,
  ConversationPermission,
  ContactPermission,
  CredentialPermission,
  SettingPermission,
  UserPermission,
  IntegrationPermission,
  ReportPermission,
  ProductPermission,
  PropertyPermission,
  WebsitePermission,
  WebsiteTemplateRegistryPermission,
} from 'src/modules/shared/types/permissions.type';

/**
 * Props for the SidebarGroups component
 * @property variant - The sidebar variant (desktop or mobile)
 * @property onItemClick - Optional callback when an item is clicked
 */
interface SidebarGroupsProps {
  /** Desktop keeps every section available; mobile retains group toggles. */
  variant: 'desktop' | 'mobile';
  /** Hide desktop labels without replacing or moving navigation items. */
  isCollapsed?: boolean;
  /** Close a containing mobile drawer when navigating. */
  onItemClick?: () => void;
}

interface SidebarGroupItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  search?: Record<string, unknown>;
  isHidden: boolean;
  badgeCount?: number;
}

interface SidebarGroup {
  id: string;
  label: string;
  items: SidebarGroupItem[];
}

const SIDEBAR_GROUPS_OPEN_STORAGE_KEY = 'lp.ui.sidebarGroupOpen';

const readGroupOpenState = (): Record<string, boolean> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(SIDEBAR_GROUPS_OPEN_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {};
    }
    return parsed as Record<string, boolean>;
  } catch {
    return {};
  }
};

const persistGroupOpenState = (state: Record<string, boolean>) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    SIDEBAR_GROUPS_OPEN_STORAGE_KEY,
    JSON.stringify(state)
  );
};

const iconSizeByVariant: Record<'desktop' | 'mobile', string> = {
  desktop: 'h-5 w-5',
  mobile: 'h-6 w-6',
};

/**
 * SidebarGroups component that renders the main navigation groups for the sidebar.
 * Calculates visibility of items based on feature flags and user permissions internally.
 */
export const SidebarGroups: React.FC<SidebarGroupsProps> = ({
  variant,
  isCollapsed = false,
  onItemClick,
}) => {
  const { t } = useI18n();
  const clientType = useClientType();
  const matchRoute = useMatchRoute();
  const [openByGroupId, setOpenByGroupId] = React.useState<
    Record<string, boolean>
  >(() => readGroupOpenState());

  // Feature flags and settings
  const crmActive = useFeatureFlagActive(SettingName.FEATURE_FLAG_CRM);
  useFeatureFlagActive(SettingName.FEATURE_FLAG_CONVERSATIONS);
  const { setting: showReports, loading: loadingReports } = useBooleanSetting(
    SettingName.SHOW_REPORTS
  );

  // Permissions - CRM
  const { hasPermission: canReadConversations, loading: loadingConversations } =
    useHasPermissions(ConversationPermission.Read);
  const { hasPermission: canReadContacts, loading: loadingContacts } =
    useHasPermissions(ContactPermission.Read);

  // Permissions - Automation
  const { hasPermission: canReadEmployees, loading: loadingEmployees } =
    useHasPermissions(AutomationEmployeePermission.Read);
  const { hasPermission: canReadWorkflows, loading: loadingWorkflows } =
    useHasPermissions(AutomationWorkflowPermission.Read);

  // Permissions - Analytics
  const { hasPermission: canReadCalls, loading: loadingCalls } =
    useHasPermissions(CallPermission.Read);
  const {
    hasPermission: canReadReportDashboard,
    loading: loadingReportsPermission,
  } = useHasPermissions(ReportPermission.DashboardRead);

  // Permissions - Catalog
  const { hasPermission: canReadAllProperties } = useHasPermissions(
    PropertyPermission.Read
  );
  const { hasPermission: canReadScopedProperties } = useHasPermissions(
    PropertyPermission.ReadScoped
  );
  const canReadProperties = canReadAllProperties || canReadScopedProperties;
  const { hasPermission: canReadProducts, loading: loadingProducts } =
    useHasPermissions(ProductPermission.Read);

  // Permissions - Settings
  const { hasPermission: canReadUsers, loading: loadingUsers } =
    useHasPermissions(UserPermission.Read);
  const { hasPermission: canReadIntegrations, loading: loadingIntegrations } =
    useHasPermissions(IntegrationPermission.Read);
  const { hasPermission: canReadCredentials, loading: loadingCredentials } =
    useHasPermissions(CredentialPermission.Read);
  const { hasPermission: canReadSettings, loading: loadingSettings } =
    useHasPermissions(SettingPermission.Read);
  const { hasPermission: canReadWebsites, loading: loadingWebsites } =
    useHasPermissions(WebsitePermission.Read);
  const {
    hasPermission: canReadWebsiteTemplateRegistry,
    loading: loadingWebsiteTemplateRegistry,
  } = useHasPermissions(WebsiteTemplateRegistryPermission.Read);

  // Merge all loading states
  const isLoading =
    loadingReports ||
    loadingConversations ||
    loadingContacts ||
    loadingEmployees ||
    loadingWorkflows ||
    loadingCalls ||
    loadingReportsPermission ||
    loadingProducts ||
    loadingUsers ||
    loadingIntegrations ||
    loadingCredentials ||
    loadingSettings ||
    loadingWebsites ||
    loadingWebsiteTemplateRegistry;

  const iconClassName = iconSizeByVariant[variant];

  const handleItemClick = () => {
    if (onItemClick) {
      onItemClick();
    }
  };

  const renderGroupHeader = (params: {
    groupId: string;
    label: string;
    isOpen: boolean;
    isActive: boolean;
    canToggle: boolean;
    onToggle: () => void;
  }) => {
    const { groupId, label, isOpen, isActive, canToggle, onToggle } = params;
    const caretRotationClass = isOpen ? 'rotate-0' : '-rotate-90';
    const caretDisabledClass = canToggle ? '' : 'opacity-30';

    if (variant === 'desktop') {
      return (
        <div className="relative flex h-8 items-center px-3">
          <span data-sidebar-divider aria-hidden="true" />
          <span data-sidebar-label aria-hidden={isCollapsed}>
            <Text
              as="span"
              size="tiny"
              color={isActive ? 'primary' : 'secondary'}
              className="uppercase tracking-[0.08em]"
            >
              {label}
            </Text>
          </span>
        </div>
      );
    }

    return (
      <button
        type="button"
        className="mt-2 px-4 w-full flex items-center justify-between"
        onClick={onToggle}
        disabled={!canToggle}
        aria-expanded={isOpen}
        aria-controls={`sidebar-group-${groupId}`}
      >
        <Text
          size="tiny"
          color={isActive ? 'primary' : 'secondary'}
          className="uppercase tracking-[0.08em]"
        >
          {label}
        </Text>
        <PiCaretDown
          className={`h-4 w-4 text-secondary-600 transition-transform ${caretRotationClass} ${caretDisabledClass}`}
        />
      </button>
    );
  };

  const isRealEstate = clientType === 'REAL_ESTATE';

  const groups: SidebarGroup[] = [
    {
      id: 'crm',
      label: t('sidebar.crm'),
      items: [
        {
          id: 'communications',
          label: t('sidebar.communications'),
          icon: PiChatsCircleLight,
          to: CommunicationsIndexRoute.to,
          isHidden: !crmActive || !canReadConversations,
        },
        {
          id: 'agenda',
          label: t('sidebar.agenda'),
          icon: PiCalendarLight,
          to: CalendarIndexRoute.to,
          isHidden: !crmActive,
        },
        {
          id: 'contacts',
          label: t('sidebar.contacts'),
          icon: PiBookOpenTextLight,
          to: ContactsIndexRoute.to,
          isHidden: !crmActive || !canReadContacts,
        },
      ],
    },
    {
      id: 'automation',
      label: t('sidebar.automation'),
      items: [
        {
          id: 'agents',
          label: t('sidebar.agents'),
          icon: AiRobotOutlineIcon,
          to: EmployeesIndexRoute.to,
          isHidden: !canReadEmployees,
        },
        {
          id: 'automations',
          label: t('sidebar.automations'),
          icon: PiCirclesThreeLight,
          to: AutomationsIndexRoute.to,
          isHidden: !canReadWorkflows,
        },
      ],
    },
    {
      id: 'analytics',
      label: t('sidebar.analytics'),
      items: [
        {
          id: 'calls',
          label: t('sidebar.calls'),
          icon: PiPhoneLight,
          to: CallsIndexRoute.to,
          isHidden: !canReadCalls,
        },
        ...(showReports && canReadReportDashboard
          ? [
              {
                id: 'reports',
                label: t('sidebar.reports'),
                icon: PiChartBarLight,
                to: ReportsPanelIndexRoute.to,
                isHidden: false,
              },
            ]
          : []),
      ],
    },
    {
      id: 'catalog',
      label: t('sidebar.catalog'),
      items: [
        {
          id: 'properties',
          label: t('sidebar.properties'),
          icon: PiBuildingLight,
          to: CatalogPropertiesRoute.to,
          isHidden: !canReadProperties,
        },
        ...(!isRealEstate
          ? [
              {
                id: 'products',
                label: t('sidebar.products'),
                icon: PiPackageLight,
                to: CatalogProductsRoute.to,
                isHidden: !canReadProducts,
              },
            ]
          : []),
      ],
    },
    {
      id: 'settings',
      label: t('sidebar.settings'),
      items: [
        {
          id: 'settings-preferences',
          label: t('sidebar.settings-preferences'),
          icon: PiUserCircleLight,
          to: SettingsPreferencesRoute.to,
          isHidden: false,
        },
        {
          id: 'settings-tenant',
          label: t('settings.tenant.title'),
          icon: PiGearLight,
          to: SettingsTenantRoute.to,
          isHidden: !canReadSettings,
        },
        {
          id: 'settings-communications',
          label: t('sidebar.settings-communications'),
          icon: PiTreeStructureLight,
          to: SettingsCommunicationsRoute.to,
          isHidden: !canReadIntegrations,
        },
        {
          id: 'settings-notifications',
          label: t('sidebar.settings-notifications'),
          icon: PiBellLight,
          to: SettingsNotificationsRoute.to,
          isHidden: !canReadIntegrations,
        },
        {
          id: 'settings-users',
          label: t('sidebar.settings-users'),
          icon: PiUsersLight,
          to: UsersIndexRoute.to,
          isHidden: !canReadUsers,
        },
        {
          id: 'settings-integrations',
          label: t('sidebar.settings-integrations'),
          icon: PiPlugLight,
          to: SettingsIntegrationsRoute.to,
          isHidden: !canReadIntegrations,
        },
        {
          id: 'settings-custom-fields',
          label: t('sidebar.settings-custom-fields'),
          icon: PiTableLight,
          to: SettingsCustomFieldsRoute.to,
          isHidden: !canReadIntegrations,
        },
        {
          id: 'settings-credentials',
          label: t('sidebar.settings-credentials'),
          icon: PiKeyLight,
          to: SettingsCredentialsRoute.to,
          isHidden: !canReadCredentials,
        },
        {
          id: 'settings-websites',
          label: 'Sitios web',
          icon: PiGlobeLight,
          to: SettingsWebsitesRoute.to,
          isHidden: !canReadWebsites || !isRealEstate,
        },
        {
          id: 'settings-website-template-registry',
          label: 'Registry web',
          icon: PiPackageLight,
          to: SettingsWebsiteTemplateRegistryRoute.to,
          isHidden: !canReadWebsiteTemplateRegistry || !isRealEstate,
        },
      ],
    },
  ];

  const visibleGroups = groups
    .map((group) => ({
      ...group,
      visibleItems: group.items.filter((item) => !item.isHidden),
    }))
    .filter((group) => group.visibleItems.length > 0);

  const activeGroupIds = visibleGroups
    .filter((group) =>
      group.visibleItems.some((item) =>
        matchRoute({ to: item.to, fuzzy: true })
      )
    )
    .map((group) => group.id);

  const activeGroupIdsKey = activeGroupIds.join('|');

  React.useEffect(() => {
    if (variant === 'desktop' || !activeGroupIdsKey) return;
    const activeIds = activeGroupIdsKey.split('|').filter(Boolean);
    if (activeIds.length === 0) return;

    setOpenByGroupId((prev) => {
      let changed = false;
      const next: Record<string, boolean> = { ...prev };

      for (const groupId of activeIds) {
        if (next[groupId] !== true) {
          next[groupId] = true;
          changed = true;
        }
      }

      if (!changed) return prev;
      persistGroupOpenState(next);
      return next;
    });
  }, [activeGroupIdsKey, variant]);

  if (isLoading) {
    return <SidebarGroupsSkeleton variant={variant} />;
  }

  return (
    <>
      {visibleGroups.map((group) => {
        // Check if group has any visible items
        const visibleItems = group.visibleItems;

        const isGroupActive = visibleItems.some((item) =>
          matchRoute({ to: item.to, fuzzy: true })
        );

        const defaultOpen = group.id !== 'settings';
        const isOpen = openByGroupId[group.id] ?? defaultOpen;
        const canToggle = !isGroupActive;

        const handleToggle = () => {
          if (isGroupActive) return;
          setOpenByGroupId((prev) => {
            const next = {
              ...prev,
              [group.id]: !(prev[group.id] ?? defaultOpen),
            };
            persistGroupOpenState(next);
            return next;
          });
        };

        return (
          <div
            key={group.id}
            className="flex flex-col gap-1.5"
            role="group"
            aria-label={group.label}
          >
            {renderGroupHeader({
              groupId: group.id,
              label: group.label,
              isOpen: isGroupActive || isOpen,
              isActive: isGroupActive,
              canToggle,
              onToggle: handleToggle,
            })}
            {(variant === 'desktop' || isGroupActive || isOpen) && (
              <div
                id={`sidebar-group-${group.id}`}
                className="flex flex-col gap-1.5"
              >
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <SidebarButton
                      key={item.id}
                      to={item.to}
                      search={item.search}
                      icon={<Icon className={iconClassName} />}
                      label={item.label}
                      isCollapsed={isCollapsed}
                      badgeCount={item.badgeCount}
                      onClick={handleItemClick}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};
